import { Loader } from '../../components/Loader';
import {
  Button,
  ButtonVariants,
  Input,
  LinkButton,
  Typography,
} from '../../components/primitives';
import { Iconography } from '../../components/primitives/Iconography';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '../../components/ui/form';
import { InputType } from '../../components/ui/input';
import {
  CouponError,
  useCheckoutContext,
} from '../../context/checkout.context';
import { testId } from '../../utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import { AxiosError } from 'axios';
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const LoadedElement = ({
  isLoading,
  isError,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  children: React.JSX.Element;
}) => {
  if (isLoading || isError) return <Loader variant="bars" size="sm" />;

  return children;
};

type DiscountProps = {
  'data-testid': string;
  currencyFormatter: (value?: Cornerstone.MonetaryAmount) => string;
};

type DiscountEntryProps = {
  'data-testid': string;
  isLoading: boolean;
  applyCoupon: (couponCode: string) => void;
  discountsUsed: Cornerstone.CheckoutDiscountItem[];
  couponError: CouponError;
};

const DiscountEntry: React.FC<DiscountEntryProps> = ({
  isLoading,
  applyCoupon,
  discountsUsed,
  couponError,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.checkout' });

  const DiscountInputSchema = z.object({
    discountCode: z.string(),
  });
  type DiscountInputSchema = z.infer<typeof DiscountInputSchema>;

  const form = useForm<DiscountInputSchema>({
    resolver: zodResolver(DiscountInputSchema),
    mode: 'onChange',
    defaultValues: {
      discountCode: '',
    },
    values: {
      discountCode: '',
    },
  });

  const { reset: formReset, watch } = form;

  const onSubmit = React.useCallback(
    (data: DiscountInputSchema) => {
      applyCoupon(data.discountCode);
      formReset(undefined, { keepValues: true });
    },
    [formReset, applyCoupon],
  );

  const discountCode = watch('discountCode');

  const appliedCouponMode = discountsUsed[0]?.applicationMode;

  const discountCodeApplied = React.useMemo(() => {
    return (
      discountCode &&
      !!discountsUsed.find((discount) => discount.discountCode === discountCode)
    );
  }, [discountsUsed, discountCode]);

  const getCouponErrorMessage = React.useCallback(
    (mutationError: CouponError, previousDiscount?: string | undefined) => {
      if (!mutationError) {
        return null;
      }

      const errorCode = (mutationError as AxiosError<any>).response?.data
        ?.errorCode;

      if (previousDiscount) {
        return t('discount_previous_code_applied');
      }

      switch (errorCode) {
        case Cornerstone.ErrorCode.NO_LONGER_VALID:
          return t('discount_expired');
        case Cornerstone.ErrorCode.NO_SUPPORTED_SKUS_IN_CART:
        case Cornerstone.ErrorCode.CART_DOES_NOT_QUALIFY:
          return t('discount_not_applicable');
        case Cornerstone.ErrorCode.USAGE_LIMIT_REACHED:
          return t('discount_already_claimed');
        default:
          return t('discount_error_generic');
      }
    },
    [t],
  );

  const couponSuccessMessage = React.useMemo(() => {
    switch (appliedCouponMode) {
      case Cornerstone.DiscountApplicationMode.BULK:
        return t('volume_discount');
      case Cornerstone.DiscountApplicationMode.AUTOMATIC:
        return t('automatic_discount');
      default:
        return t('discount_success');
    }
  }, [appliedCouponMode, t]);

  const existingCoupon =
    discountsUsed[0] &&
    discountsUsed[0].applicationMode ===
      Cornerstone.DiscountApplicationMode.COUPON
      ? discountsUsed[0].discountCode
      : '';

  const onClickClear = React.useCallback(() => {
    if (couponError) {
      const prevCoupon = existingCoupon ?? '';
      applyCoupon(prevCoupon);
      formReset({ discountCode: prevCoupon });
    } else {
      applyCoupon('');
      formReset({ discountCode: '' });
    }
  }, [couponError, existingCoupon, applyCoupon, formReset]);

  const formSubmit = form.handleSubmit(onSubmit);

  return (
    <div className="flex flex-col">
      <Form {...form}>
        <form onSubmit={formSubmit} className="flex flex-col gap-6">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex-grow flex flex-col gap-1">
              <FormField
                control={form.control}
                name="discountCode"
                render={({ field }) => (
                  <FormItem className="flex-grow">
                    <FormControl>
                      <Input
                        {...testId(dataTestId, 'discount-input')}
                        type={InputType.TEXT}
                        {...field}
                        placeholder=""
                        onClickClear={onClickClear}
                        invalid={!!couponError}
                        disabled={isLoading}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              {!!couponError && (
                <div className="flex flex-row items-center">
                  <Iconography
                    variant="error"
                    className="text-red-dark text-base"
                  />
                  <Typography
                    {...testId(dataTestId, 'discount_error')}
                    className="text-xs"
                    variant="error"
                  >
                    {getCouponErrorMessage(couponError, existingCoupon)}
                  </Typography>
                </div>
              )}
              {appliedCouponMode && !couponError && (
                <div className="flex flex-row">
                  <Iconography
                    variant="check_circle"
                    className="text-green-dark text-base pl-0"
                  />
                  <Typography
                    {...testId(dataTestId, 'discount_success')}
                    className="text-xs"
                    variant="success-note"
                  >
                    {couponSuccessMessage}
                  </Typography>
                </div>
              )}
            </div>
            <Button
              variant={ButtonVariants.Secondary}
              {...testId(dataTestId, 'apply-discount')}
              size="sm"
              disabled={
                isLoading || !form.formState.isValid || !!discountCodeApplied
              }
            >
              {discountCodeApplied
                ? t('applied_discount')
                : t('apply_discount')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export const Discount: React.FC<DiscountProps> = ({
  'data-testid': dataTestId,
  currencyFormatter,
}) => {
  const {
    checkoutData,
    checkoutIsLoading,
    checkoutIsError,
    errors,
    applyCoupon,
  } = useCheckoutContext();
  const { t } = useTranslation(undefined, { keyPrefix: 'route.checkout' });

  const [forceShowDiscountEntry, setForceShowDiscountEntry] =
    React.useState(false);

  const onAddDiscount = React.useCallback(() => {
    setForceShowDiscountEntry(true);
  }, []);

  const showDiscountEntry =
    (checkoutData.discountsUsed?.length ?? 0) > 0 || forceShowDiscountEntry;

  const hasDiscount = checkoutData.lineItemDiscountTotal.amount > 0;
  const discountAmount = checkoutData.lineItemDiscountTotal;

  return (
    <div>
      <div className="flex justify-between">
        <Typography variant="body-semibold">{t('discount')}</Typography>

        {!showDiscountEntry && (
          <LinkButton
            onClick={onAddDiscount}
            {...testId(dataTestId, 'add_discount')}
          >
            {t('add_discount')}
          </LinkButton>
        )}

        {hasDiscount && (
          <LoadedElement
            isLoading={checkoutIsLoading}
            isError={checkoutIsError}
          >
            <DiscountText
              {...testId(dataTestId, 'discountLineItems')}
              currencyFormatter={currencyFormatter}
              discount={{
                amount: -1 * discountAmount.amount,
                currency: discountAmount.currency,
              }}
            />
          </LoadedElement>
        )}
      </div>
      {showDiscountEntry && (
        <DiscountEntry
          {...testId(dataTestId, 'discount-entry')}
          isLoading={checkoutIsLoading}
          applyCoupon={applyCoupon}
          discountsUsed={checkoutData.discountsUsed}
          couponError={errors.coupon}
        />
      )}
    </div>
  );
};

type DiscountTextProps = DiscountProps & {
  discount: Cornerstone.MonetaryAmount;
};
export function DiscountText({
  'data-testid': dataTestId,
  currencyFormatter,
  discount,
}: DiscountTextProps) {
  return (
    <Typography
      variant="body-semibold"
      className="text-password-strong"
      {...testId(dataTestId)}
    >
      {currencyFormatter({
        amount: discount.amount,
        currency: discount.currency,
      })}
    </Typography>
  );
}
